import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Col, Row, message } from "antd";
import { InputField, SelectInput, DatePickerField, AntdButton } from "../../blueplanitUtils/InputField";
import Modal from '../../blueplanitUtils/Modal';
import dayjs from "dayjs";
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { getOptionsByPath } from "../../utilsdata";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchHolidays, updateHoliday } from "../../Slices/holidaySlice";
import { createHoliday } from "../../Slices/holidaySlice";
import { useDispatch } from "react-redux";
import { ADD_EDIT_EMPLOYEE, EMPLOYEE_DEPARTMENT, HOLIDAYS, HOLIDAYS_TYPE } from "../../Dropdownmanager";

export const AddHoliday = (props) => {
  const dispatch = useDispatch();
  const { users, selectedHoliday, setSelectedHoliday, isOpen, onClose, refetch } = props;
  const [forms, setForms] = useState([{ name: "", department: [], date: null, type: "" }]);
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);

  useEffect(() => {
    if (selectedHoliday) {
      setForms([{
        name: selectedHoliday.name,
        department: selectedHoliday.department ? selectedHoliday.department.split(',') : [],
        date: selectedHoliday.date
          ? dayjs(selectedHoliday.date.length > 10 ? dayjs.utc(selectedHoliday.date).local() : dayjs(selectedHoliday.date, "YYYY-MM-DD").startOf('day'))
          : null,
        type: selectedHoliday.type,
      }]);
    }
  }, [selectedHoliday]);

  const handleDepartmentChange = (selectedValues, index) => {
    setForms((prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms[index] = {
        ...updatedForms[index],
        department: selectedValues,
        type: "", // ✅ Reset type when department changes
      };
      return updatedForms;
    });
  };

  const handleTypeChange = (value, index) => {
    const updatedForms = [...forms];
    updatedForms[index].type = value;
    setForms(updatedForms);
  };


  const handleChange = (e, index) => {
    const { name, value } = e.target;
    setForms((prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms[index] = { ...updatedForms[index], [name]: value };
      return updatedForms;
    });
  };

  const handleDateChange = (date, index) => {
    setForms((prevForms) => {
      const updatedForms = [...prevForms];
      updatedForms[index] = {
        ...updatedForms[index],
        date: date ? date.format("YYYY-MM-DD") : null,
      };
      return updatedForms;
    });
  };

  const handleRemoveRow = (index) => {
    setForms((prevForms) => prevForms.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setForms([{ name: "", department: [], date: null }]);
    setSelectedHoliday(null);
    onClose();
  };

  const getDateOnly = (date) => dayjs(date).format('YYYY-MM-DD');

  const checkDateConflict = (form, users, selectedHolidayId = null) => {
    const formDateStr = getDateOnly(form.date);
    const formDepartments = new Set(form.department.map(d => d.trim()));

    const hasOverlap = (depString) => {
      const depArray = depString.split(',').map(d => d.trim());
      const depSet = new Set(depArray);
      for (let dept of formDepartments) {
        if (depSet.has(dept)) return true;
      }
      return false;
    };

    const conflict = users.find((holiday) => {
      const holidayDateStr = getDateOnly(holiday.date);
      return formDateStr === holidayDateStr &&
        hasOverlap(holiday.department) &&
        (!selectedHolidayId || holiday._id !== selectedHolidayId);
    });
    return !!conflict;
  };
  const isFormsUnchanged = (forms, selectedHoliday) => {
    if (forms.length !== 1) return false; // Only care about edit mode (single form)

    const form = forms[0];

    const selectedDepartments = (selectedHoliday.department || '')
      .split(',')
      .map(dep => dep.trim())
      .filter(Boolean)
      .sort();

    const formDepartments = [...(form.department || [])]
      .map(dep => dep.trim())
      .filter(Boolean)
      .sort();

    const isNameSame = (form.name || '') === (selectedHoliday.name || '');
    const isDateSame = dayjs(form.date).format('YYYY-MM-DD') === dayjs(selectedHoliday.date).format('YYYY-MM-DD');
    const isDepartmentSame = JSON.stringify(selectedDepartments) === JSON.stringify(formDepartments);
    const isType = (form.type || '') === (selectedHoliday.type || '');

    return isNameSame && isDateSame && isDepartmentSame && isType;
  };

  let lastSubmitTime = 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentTime = new Date().getTime();
    if (currentTime - lastSubmitTime < 3000) return;
    lastSubmitTime = currentTime;

    if (selectedHoliday && isFormsUnchanged(forms, selectedHoliday)) {
      message.error('No changes detected. Please modify something before submitting.');
      return;
    }

    for (let i = 0; i < forms.length; i++) {
      const form = forms[i];
      if (!form.name || form.department.length === 0 || !form.date || !form.type) {
        message.error("Please fill all fields before submitting.");
        return;
      }

      const conflict = checkDateConflict(form, users, selectedHoliday ? selectedHoliday._id : null);
      if (conflict) {
        message.error(`A holiday already exists on ${dayjs(form.date).format('DD/MM/YYYY')} with the same department. Please choose a different date or department.`);
        return;
      }
    }

    const successMessages = new Set();
    const errorMessages = new Set();
    let allMutationPromises = [];

    try {
      for (const form of forms) {
        const departments = form.department;

        if (selectedHoliday) {
          const departmentToHolidayId = {};
          users.forEach((holiday) => {
            holiday.department.split(',').forEach((dep) => {
              departmentToHolidayId[dep.trim()] = holiday._id;
            });
          });

          const formDepartments = departments.map((d) => d.trim());

          const selectedDepartments = selectedHoliday.department
            ? selectedHoliday.department.split(',').map((d) => d.trim())
            : [];

          const removedDepartments = selectedDepartments.filter(
            (dep) => !formDepartments.includes(dep)
          );
          const addedDepartments = formDepartments.filter(
            (dep) => !selectedDepartments.includes(dep)
          );
          const keptDepartments = formDepartments.filter((dep) =>
            selectedDepartments.includes(dep)
          );

          // ✅ Update kept departments
          if (keptDepartments.length > 0) {
            allMutationPromises.push(
              dispatch(updateHoliday({
                id: selectedHoliday._id,
                name: form.name,
                department: keptDepartments.join(','),
                date: form.date,
                type: form.type,
              })).unwrap().then((res) => {
                if (res.status === 200 && res.message) successMessages.add(res.message);
                else if (res.status === 400 && res.message) errorMessages.add(res.message);
              }).catch((err) => errorMessages.add(err))
            );
          }

          // ✅ Update removed & added departments
          if (removedDepartments.length === addedDepartments.length) {
            for (let i = 0; i < addedDepartments.length; i++) {
              const removedDep = removedDepartments[i];
              const addedDep = addedDepartments[i];
              const holidayId = departmentToHolidayId[removedDep];

              if (holidayId) {
                allMutationPromises.push(
                  dispatch(updateHoliday({
                    id: holidayId,
                    name: form.name,
                    department: addedDep,
                    date: form.date,
                    type: form.type,
                  })).unwrap().then((res) => {
                    if (res.status === 200 && res.message) successMessages.add(res.message);
                    else if (res.status === 400 && res.message) errorMessages.add(res.message);
                  }).catch((err) => errorMessages.add(err))
                );
              } else {
                allMutationPromises.push(
                  dispatch(createHoliday({
                    name: form.name,
                    department: addedDep,
                    date: form.date,
                    type: form.type,
                  })).unwrap().then((res) => {
                    if (res.status === 200 && res.message) successMessages.add(res.message);
                    else if (res.status === 400 && res.message) errorMessages.add(res.message);
                  }).catch((err) => errorMessages.add(err))
                );
              }
            }
          } else {
            for (const newDep of addedDepartments) {
              allMutationPromises.push(
                dispatch(createHoliday({
                  name: form.name,
                  department: newDep,
                  date: form.date,
                  type: form.type,
                })).unwrap().then((res) => {
                  if (res.status === 200 && res.message) successMessages.add(res.message);
                  else if (res.status === 400 && res.message) errorMessages.add(res.message);
                }).catch((err) => errorMessages.add(err))
              );
            }
          }
        } else {
          // ✅ New holiday creation
          allMutationPromises.push(
            dispatch(createHoliday({
              name: form.name,
              department: departments.join(','),
              date: form.date,
              type: form.type,
            })).unwrap().then((res) => {
              if (res.status === 200 && res.message) successMessages.add(res.message);
              else if (res.status === 400 && res.message) errorMessages.add(res.message);
            }).catch((err) => errorMessages.add(err))
          );
        }
      }

      await Promise.all(allMutationPromises);

      // ✅ Display messages once each
      if (successMessages.size > 0) {
        message.success(Array.from(successMessages).join(' | '));
      }
      if (errorMessages.size > 0) {
        message.error(Array.from(errorMessages).join(' | '));
      }

      await dispatch(fetchHolidays({ department: loginUser?.department, role: loginUser?.role }));
      onClose();
      resetForm();

    } catch (error) {
      console.error("Error during holiday operation:", error);
      message.error("Failed to process holiday request. Please try again.");
    }
  };


  return (
    <Modal
      id="add_holiday"
      title={selectedHoliday ? "Edit Holiday" : "Add Holiday"}
      isOpen={`${isOpen ? "show" : " "}`}
      style={{ display: isOpen ? "block" : "none" }}
      handleModalClose={resetForm}
      handleSubmit={handleSubmit}
      size="lg"
      isUserDataValid={false}
    >
      {forms.map((form, index) => (
        <div
          key={index}
          style={{
            marginBottom: "16px",
            borderBottom: "1px dashed #ccc",
            paddingBottom: "8px",
          }}
        >
          <Row gutter={32} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <SelectInput
                required
                label="Department"
                placeholder="Select Department"
                mode="multiple"
                allowClear
                options={getOptionsByPath(
                  NestedDatabucket,
                  [ADD_EDIT_EMPLOYEE, EMPLOYEE_DEPARTMENT]
                )}
                onChange={(selectedValues) =>
                  handleDepartmentChange(selectedValues, index)
                }
                value={form.department}
                style={{ width: "100%" }}
              />
            </Col>

            <Col span={6}>
              <InputField
                label="Holiday Name"
                required
                placeholder="Holiday Name"
                type="text"
                name="name"
                value={form.name}
                onChange={(e) => handleChange(e, index)}
              />
            </Col>

            <Col span={6}>
              <DatePickerField
                label="Date"
                required
                name="date"
                value={form.date ? dayjs(form.date) : null}
                onChange={(date) => handleDateChange(date, index)}
                format="DD/MM/YYYY"
                placeholder="DD/MM/YYYY"
                getPopupContainer={(trigger) => trigger.parentElement}
                onFocus={(e) => e.preventDefault()}
                autoFocus={false}
              />
            </Col>

            {/* 🔹 New Type field */}
            <Col span={6}>
              <SelectInput
                required
                label="Type"
                name="type"
                placeholder="Select Type"
                options={getOptionsByPath(
                  NestedDatabucket,
                  [HOLIDAYS, HOLIDAYS_TYPE]
                )}
                onChange={(value) => handleTypeChange(value, index)}
                value={form.type}
                style={{ width: "100%" }}
              />
            </Col>

            <Col span={3}>
              <div
                style={{ display: "flex", alignItems: "center", height: "100%" }}
              >
                {forms.length > 1 && (
                  <AntdButton
                    type="button"
                    className="fa fa-trash"
                    onClick={() => handleRemoveRow(index)}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: 'rgba(248, 99, 114, 1)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  />
                )}
              </div>
            </Col>
          </Row>
        </div>
      ))}

      {!selectedHoliday && forms.length < 5 && (
        <div className="add-more" style={{ marginTop: "8px" }}>
          <AntdButton
            type="button"
            label="Add more"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() =>
              setForms([
                ...forms,
                { name: "", department: [], date: null, type: "" }, // 🔹 include type in default
              ])
            }
            style={{
              background: "none",
              border: "none",
              padding: "2px 4px",
              color: "#1677ff", // Ant Design primary blue
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: 400,
              fontSize: "13px",
              textDecoration: "none",
            }}
          />
        </div>
      )}
    </Modal>
  );

};